import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import * as Icons from 'lucide-react';
import { useTranslation } from '../../hooks/useTranslation';
import { useState, useEffect } from 'react';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';

export default function ProfileMenu() {
  const { currentUser, signOut } = useAuth();
  const { t } = useTranslation();
  const [userData, setUserData] = useState(null);

  useEffect(() => {
    async function fetchUserData() {
      if (currentUser) {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        if (userDoc.exists()) {
          setUserData(userDoc.data());
        }
      }
    }
    fetchUserData();
  }, [currentUser]);

  if (!currentUser) return null;

  const displayName = userData?.displayName || currentUser.displayName || currentUser.email || 'User';

  return (
    <Menu as="div" className="relative ml-3">
      <div>
        <Menu.Button className="flex items-center max-w-xs text-sm rounded-full focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500">
          <span className="sr-only">{t('profile.menu.title')}</span>
          {(userData?.photoURL || currentUser?.photoURL) ? (
            <>
              <img
                src={userData?.photoURL || currentUser.photoURL}
                alt={displayName}
                className="h-8 w-8 rounded-full object-cover"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'flex';
                }}
              />
              <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 hidden items-center justify-center text-sm font-medium text-indigo-600">
                {displayName.charAt(0).toUpperCase()}
              </div>
            </>
          ) : (
            <div className="h-8 w-8 rounded-full bg-gradient-to-br from-indigo-100 to-indigo-200 flex items-center justify-center text-sm font-medium text-indigo-600">
              {displayName.charAt(0).toUpperCase()}
            </div>
          )}
        </Menu.Button>
      </div>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="origin-top-right absolute right-0 mt-2 w-48 rounded-md shadow-lg py-1 bg-white ring-1 ring-black ring-opacity-5 focus:outline-none">
          <Menu.Item>
            {({ active }) => (
              <Link
                to="/profile"
                className={`${
                  active ? 'bg-gray-100' : ''
                } block px-4 py-2 text-sm text-gray-700`}
              >
                <div className="flex items-center">
                  <Icons.User className="mr-3 h-4 w-4" />
                  {t('profile.menu.profile')}
                </div>
              </Link>
            )}
          </Menu.Item>
          <Menu.Item>
            {({ active }) => (
              <button
                onClick={signOut}
                className={`${
                  active ? 'bg-gray-100' : ''
                } block w-full text-left px-4 py-2 text-sm text-gray-700`}
              >
                <div className="flex items-center">
                  <Icons.LogOut className="mr-3 h-4 w-4" />
                  {t('profile.menu.signOut')}
                </div>
              </button>
            )}
          </Menu.Item>
        </Menu.Items>
      </Transition>
    </Menu>
  );
} 